import { combineSpans, DiagnosticBag, err, ok } from "../diagnostics.js";
import { lex } from "./lexer.js";
class Parser {
    constructor(source, tokens) {
        this.source = source;
        this.tokens = tokens;
        this.pos = 0;
        this.diagnostics = new DiagnosticBag();
    }
    parseScript() {
        const statements = [];
        const start = this.peek().span;
        while (!this.isEof()) {
            this.consumeSemicolons();
            if (this.isEof())
                break;
            const stmt = this.parseStatement();
            if (stmt !== undefined)
                statements.push(stmt);
            if (!this.matchPunctuation(';') && !this.isEof()) {
                this.diagnostics.add('PARSE_EXPECTED_TOKEN', "Expected ';' between statements", this.peek().span);
                this.synchronizeStatement();
            }
        }
        const end = statements.length > 0 ? statements[statements.length - 1].span : start;
        const script = { kind: 'script', statements, span: combineSpans(start, end) };
        return this.diagnostics.hasErrors() ? err(this.diagnostics.all()) : ok(script);
    }
    parseStatement() {
        const tok = this.peek();
        if (this.matchKeyword('CREATE')) {
            const createTok = this.previous();
            if (this.matchKeyword('DATABASE'))
                return this.parseCreateDatabase(createTok.span);
            if (this.matchKeyword('SCHEMA'))
                return this.parseCreateSchema(createTok.span);
            if (this.matchKeyword('TABLEGROUP'))
                return this.parseCreateTableGroup(createTok.span);
            this.expected('DATABASE, SCHEMA or TABLEGROUP');
            return undefined;
        }
        if (this.matchKeyword('ADD')) {
            const start = this.previous().span;
            if (this.matchKeyword('SCHEMA'))
                return this.parseAddMember(start, 'schema');
            if (this.matchKeyword('TABLEGROUP'))
                return this.parseAddMember(start, 'tablegroup');
            this.expected('SCHEMA or TABLEGROUP');
            return undefined;
        }
        if (this.matchKeyword('ALTER'))
            return this.parseAlterSchema(this.previous().span);
        if (this.matchKeyword('UPDATE')) {
            const start = this.previous().span;
            if (this.matchKeyword('REF'))
                return this.parseUpdateRef(start);
            if (this.matchKeyword('SCHEMA'))
                return this.parseUpdateSchema(start);
            return this.parseUpdate(start);
        }
        if (this.matchKeyword('DELETE'))
            return this.parseDelete(this.previous().span);
        if (this.matchKeyword('BUNDLE'))
            return this.parseBundle(this.previous().span);
        if (this.matchKeyword('SET')) {
            const start = this.previous().span;
            if (this.matchKeyword('VIEW'))
                return this.parseSetView(start);
            this.expected('VIEW');
            return undefined;
        }
        if (this.matchKeyword('INSERT'))
            return this.parseInsert(this.previous().span);
        if (this.matchKeyword('SELECT'))
            return this.parseSelect(this.previous().span);
        if (this.matchKeyword('EXPLAIN')) {
            const explainStart = this.previous().span;
            if (!this.matchKeyword('LOG')) {
                this.expected('LOG');
                return undefined;
            }
            const stmt = this.parseLog(this.previous().span);
            stmt.explain = true;
            stmt.span = combineSpans(explainStart, stmt.span);
            return stmt;
        }
        if (this.matchKeyword('LOG'))
            return this.parseLog(this.previous().span);
        this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected token '${tok.text}'`, tok.span);
        this.synchronizeStatement();
        return undefined;
    }
    diagnosticsList() {
        return this.diagnostics;
    }
    parseCreateDatabase(start) {
        const nameTok = this.expectIdentifierToken('database name');
        let end = nameTok.span;
        let seed;
        if (this.matchKeyword('SEED')) {
            const seedTok = this.expectKind('string', 'SEED value');
            seed = seedTok.value;
            end = seedTok.span;
        }
        const creators = [];
        if (this.matchKeyword('CREATORS')) {
            this.expectPunctuation('(');
            if (!this.checkPunctuation(')')) {
                do {
                    creators.push(this.parseValue());
                } while (this.matchPunctuation(','));
            }
            end = this.expectPunctuation(')').span;
        }
        const stmt = { kind: 'create-database', name: nameTok.text, creators, span: combineSpans(start, end) };
        if (seed !== undefined)
            stmt.seed = seed;
        return stmt;
    }
    parseCreateSchema(start) {
        const name = this.expectIdentifierText('schema name');
        const creators = [];
        if (this.matchKeyword('CREATORS')) {
            this.expectPunctuation('(');
            if (!this.checkPunctuation(')')) {
                do {
                    creators.push(this.parseValue());
                } while (this.matchPunctuation(','));
            }
            this.expectPunctuation(')');
        }
        this.expectKeyword('AS');
        this.expectPunctuation('(');
        const tables = [];
        while (!this.checkPunctuation(')') && !this.isEof()) {
            tables.push(this.parseTableDecl());
            if (!this.matchPunctuation(','))
                break;
        }
        const close = this.expectPunctuation(')');
        return { kind: 'create-schema', name, creators, tables, span: combineSpans(start, close.span) };
    }
    parseTableDecl() {
        const start = this.expectKeyword('TABLE').span;
        const name = this.expectIdentifierText('table name');
        this.expectPunctuation('(');
        const columns = [];
        while (!this.checkPunctuation(')') && !this.isEof()) {
            columns.push(this.parseColumnDecl());
            if (!this.matchPunctuation(','))
                break;
        }
        let end = this.expectPunctuation(')').span;
        const options = [];
        while (!this.isEof() && !this.checkPunctuation(',') && !this.checkPunctuation(')') && !this.checkPunctuation(';')) {
            const opt = this.parseTableOption();
            if (opt === undefined)
                break;
            end = opt.span;
            options.push(opt);
        }
        this.validateAllowRules(options.filter((opt) => opt.kind === 'allow-rule'));
        return { name, columns, options, span: combineSpans(start, end) };
    }
    parseColumnDecl() {
        const start = this.peek().span;
        const name = this.expectIdentifierText('column name');
        if (name === 'uuid') {
            this.diagnostics.add('PARSE_RESERVED_NAME', "column name 'uuid' is reserved", combineSpans(start, this.peek().span));
        }
        const typeToken = this.advance();
        const type = this.columnTypeFromToken(typeToken);
        let nullable = false;
        let defaultValue;
        let pub = false;
        let readonly = false;
        let references;
        let end = typeToken.span;
        const column = { name, type, nullable, pub, readonly, span: combineSpans(start, end) };
        this.parseColumnModifiers(column);
        return column;
    }
    parseColumnModifiers(column) {
        let end = column.span;
        while (!this.isEof() && !this.checkPunctuation(',') && !this.checkPunctuation(')')) {
            if (this.matchKeyword('NULL')) {
                column.nullable = true;
                end = this.previous().span;
            }
            else if (this.matchKeyword('DEFAULT')) {
                column.defaultValue = this.parseValue();
                end = column.defaultValue.span;
            }
            else if (this.matchKeyword('PUB')) {
                column.pub = true;
                end = this.previous().span;
            }
            else if (this.matchKeyword('READONLY')) {
                column.readonly = true;
                end = this.previous().span;
            }
            else if (this.matchKeyword('REFERENCES')) {
                const ref = this.expectIdentifierToken('referenced table');
                column.references = ref.text;
                end = ref.span;
            }
            else {
                break;
            }
        }
        column.span = combineSpans(column.span, end);
    }
    parseTableOption() {
        if (this.matchKeyword('NO')) {
            const start = this.previous().span;
            this.expectKeyword('CONCURRENT');
            const end = this.expectKeyword('DELETES').span;
            return { kind: 'concurrent-deletes', value: false, span: combineSpans(start, end) };
        }
        if (this.matchKeyword('CONCURRENT')) {
            const start = this.previous().span;
            const end = this.expectKeyword('DELETES').span;
            return { kind: 'concurrent-deletes', value: true, span: combineSpans(start, end) };
        }
        if (this.matchKeyword('IDENTITY')) {
            const start = this.previous().span;
            this.expectKeyword('PROVIDER');
            let keyIdColumn = 'keyId';
            let publicKeyColumn = 'publicKey';
            let end = this.previous().span;
            if (this.matchPunctuation('(')) {
                keyIdColumn = this.expectIdentifierText('key id column');
                this.expectPunctuation(',');
                publicKeyColumn = this.expectIdentifierText('public key column');
                end = this.expectPunctuation(')').span;
            }
            return { kind: 'identity-provider', keyIdColumn, publicKeyColumn, span: combineSpans(start, end) };
        }
        if (this.checkKeyword('ALLOW')) {
            const rule = this.parseAllowRule();
            return Object.assign({ kind: 'allow-rule' }, rule);
        }
        this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected table option '${this.peek().text}'`, this.peek().span);
        this.advance();
        return undefined;
    }
    parseCreateTableGroup(start) {
        const name = this.expectIdentifierText('tablegroup name');
        let seed;
        if (this.matchKeyword('SEED')) {
            const seedTok = this.expectKind('string', 'SEED value');
            seed = seedTok.value;
        }
        this.expectKeyword('USING');
        this.expectKeyword('SCHEMA');
        const schema = this.parseNameOrHash();
        let schemaVersion;
        const bindings = [];
        let idProvider;
        let canDeploy;
        const canObserve = [];
        const initialRows = [];
        let end = schema.span;
        while (!this.isEof() && !this.checkPunctuation(';') && !this.checkPunctuation(')')) {
            if (this.matchKeyword('AT')) {
                schemaVersion = this.parseVersion();
                end = schemaVersion.span;
            }
            else if (this.matchKeyword('BIND')) {
                const bindStart = this.previous().span;
                const bindName = this.expectIdentifierText('binding name');
                this.expectOperator('=>');
                const group = this.parseNameOrHash();
                bindings.push({ name: bindName, group, span: combineSpans(bindStart, group.span) });
                end = group.span;
            }
            else if (this.matchKeyword('USING')) {
                const identities = this.expectIdentifierLike('IDENTITIES');
                if (identities.upper !== 'IDENTITIES') {
                    this.diagnostics.add('PARSE_EXPECTED_TOKEN', `Expected IDENTITIES, got '${identities.text}'`, identities.span);
                }
                const provider = this.expectIdentifierToken('identity provider');
                idProvider = provider.text;
                end = provider.span;
            }
            else if (this.matchKeyword('ALLOW')) {
                const allowStart = this.previous().span;
                if (this.matchKeyword('UPDATE')) {
                    if (this.matchKeyword('REF')) {
                        const binding = this.expectIdentifierText('binding name');
                        this.expectKeyword('IF');
                        const predicate = this.parsePredicate();
                        canObserve.push({ binding, predicate, span: combineSpans(allowStart, predicate.span) });
                        end = predicate.span;
                    }
                    else if (this.matchKeyword('SCHEMA')) {
                        this.expectKeyword('IF');
                        canDeploy = this.parsePredicate();
                        end = canDeploy.span;
                    }
                    else {
                        this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'Expected ALLOW UPDATE SCHEMA or ALLOW UPDATE REF on CREATE TABLEGROUP', this.previous().span);
                        this.advance();
                    }
                }
                else {
                    this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'Expected ALLOW UPDATE SCHEMA or ALLOW UPDATE REF on CREATE TABLEGROUP', this.previous().span);
                    this.advance();
                }
            }
            else if (this.matchKeyword('WITH')) {
                this.expectKeyword('ROWS');
                this.expectPunctuation('(');
                while (!this.checkPunctuation(')') && !this.isEof()) {
                    const row = this.parseInitialRow();
                    initialRows.push(row);
                    end = row.span;
                    if (!this.matchPunctuation(','))
                        break;
                }
                end = this.expectPunctuation(')').span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected CREATE TABLEGROUP clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'create-tablegroup', name, schema, bindings, canObserve, initialRows, span: combineSpans(start, end) };
        if (seed !== undefined)
            stmt.seed = seed;
        if (schemaVersion !== undefined)
            stmt.schemaVersion = schemaVersion;
        if (idProvider !== undefined)
            stmt.idProvider = idProvider;
        if (canDeploy !== undefined)
            stmt.canDeploy = canDeploy;
        return stmt;
    }
    parseInitialRow() {
        const startTok = this.expectIdentifierToken('initial row table');
        this.expectPunctuation('(');
        const values = [];
        while (!this.checkPunctuation(')') && !this.isEof()) {
            const column = this.expectIdentifierText('initial row column');
            this.expectOperator('=');
            values.push({ column, value: this.parseValue() });
            if (!this.matchPunctuation(','))
                break;
        }
        const end = this.expectPunctuation(')').span;
        return { table: startTok.text, values, span: combineSpans(startTok.span, end) };
    }
    parseAddMember(start, member) {
        const target = this.parseNameOrHash();
        this.expectKeyword('TO');
        const database = this.parseNameOrHash();
        let end = database.span;
        let note;
        let at;
        let author;
        while (!this.isEof() && !this.checkPunctuation(';')) {
            if (this.matchKeyword('NOTE')) {
                const tok = this.expectKind('string', 'NOTE text');
                note = tok.value;
                end = tok.span;
            }
            else if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('BY')) {
                author = this.parseAuthor();
                end = author.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected ADD clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'add-member', member, target, database, span: combineSpans(start, end) };
        if (note !== undefined)
            stmt.note = note;
        if (at !== undefined)
            stmt.at = at;
        if (author !== undefined)
            stmt.author = author;
        return stmt;
    }
    parseAlterSchema(start) {
        this.expectKeyword('SCHEMA');
        const schema = this.parseNameOrHash();
        this.expectKeyword('AS');
        this.expectPunctuation('(');
        const rules = [];
        while (!this.checkPunctuation(')') && !this.isEof()) {
            rules.push(this.parseMigrationRule());
            if (!this.matchPunctuation(','))
                break;
        }
        let end = this.expectPunctuation(')').span;
        let at;
        let author;
        while (!this.isEof() && !this.checkPunctuation(';')) {
            if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('BY')) {
                author = this.parseAuthor();
                end = author.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected ALTER SCHEMA clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'alter-schema', schema, rules, span: combineSpans(start, end) };
        if (author !== undefined)
            stmt.author = author;
        if (at !== undefined)
            stmt.at = at;
        return stmt;
    }
    parseMigrationRule() {
        const start = this.peek().span;
        if (this.matchKeyword('ADD')) {
            if (this.matchKeyword('TABLE')) {
                const table = this.parseTableDeclBody(start);
                return { kind: 'add-table', table, span: table.span };
            }
            this.expectKeyword('COLUMN');
            const { table, column } = this.parseQualifiedColumn();
            const typeTok = this.advance();
            const type = this.columnTypeFromToken(typeTok);
            const col = { name: column, type, nullable: false, pub: false, readonly: false, span: combineSpans(start, typeTok.span) };
            this.parseColumnModifiers(col);
            return { kind: 'add-column', table, column: col, span: col.span };
        }
        if (this.matchKeyword('DROP')) {
            if (this.matchKeyword('TABLE')) {
                const tok = this.expectIdentifierToken('table name');
                return { kind: 'drop-table', table: tok.text, span: combineSpans(start, tok.span) };
            }
            this.expectKeyword('COLUMN');
            const q = this.parseQualifiedColumn();
            return { kind: 'drop-column', table: q.table, column: q.column, span: combineSpans(start, q.span) };
        }
        if (this.matchKeyword('SET')) {
            if (this.matchKeyword('CONCURRENT')) {
                this.expectKeyword('DELETES');
                const table = this.expectIdentifierToken('table name');
                const valueTok = this.expectIdentifierLike('boolean');
                const value = valueTok.upper === 'TRUE';
                if (valueTok.upper !== 'TRUE' && valueTok.upper !== 'FALSE') {
                    this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'Expected true or false', valueTok.span);
                }
                return { kind: 'set-concurrent-deletes', table: table.text, value, span: combineSpans(start, valueTok.span) };
            }
            if (this.matchKeyword('FKS')) {
                const table = this.expectIdentifierToken('table name');
                this.expectPunctuation('(');
                const fks = {};
                while (!this.checkPunctuation(')') && !this.isEof()) {
                    const col = this.expectIdentifierText('FK column');
                    this.expectKeyword('REFERENCES');
                    const ref = this.expectIdentifierToken('referenced table');
                    fks[col] = ref.text;
                    if (!this.matchPunctuation(','))
                        break;
                }
                const end = this.expectPunctuation(')').span;
                return { kind: 'set-fks', table: table.text, fks, span: combineSpans(start, end) };
            }
            if (this.matchKeyword('ALLOW')) {
                this.expectKeyword('RULES');
                const table = this.expectIdentifierToken('table name');
                this.expectPunctuation('(');
                const allowRules = [];
                while (!this.checkPunctuation(')') && !this.isEof()) {
                    allowRules.push(this.parseAllowRule());
                    if (!this.matchPunctuation(','))
                        break;
                }
                const end = this.expectPunctuation(')').span;
                this.validateAllowRules(allowRules);
                return { kind: 'set-allow-rules', table: table.text, allowRules, span: combineSpans(start, end) };
            }
        }
        this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected migration rule '${this.peek().text}'`, this.peek().span);
        const tok = this.advance();
        return { kind: 'drop-table', table: tok.text, span: tok.span };
    }
    parseTableDeclBody(start) {
        const name = this.expectIdentifierText('table name');
        this.expectPunctuation('(');
        const columns = [];
        while (!this.checkPunctuation(')') && !this.isEof()) {
            columns.push(this.parseColumnDecl());
            if (!this.matchPunctuation(','))
                break;
        }
        let end = this.expectPunctuation(')').span;
        const options = [];
        while (!this.isEof() && !this.checkPunctuation(',') && !this.checkPunctuation(')') && !this.checkPunctuation(';')) {
            const opt = this.parseTableOption();
            if (opt === undefined)
                break;
            end = opt.span;
            options.push(opt);
        }
        this.validateAllowRules(options.filter((opt) => opt.kind === 'allow-rule'));
        return { name, columns, options, span: combineSpans(start, end) };
    }
    parseUpdateSchema(start) {
        const schema = this.parseNameOrHash();
        this.expectKeyword('TO');
        const version = this.parseVersion();
        this.expectKeyword('ON');
        const group = this.parseNameOrHash();
        let end = group.span;
        let at;
        let author;
        while (!this.isEof() && !this.checkPunctuation(';')) {
            if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('BY')) {
                author = this.parseAuthor();
                end = author.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected UPDATE SCHEMA clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'update-schema', schema, version, group, span: combineSpans(start, end) };
        if (author !== undefined)
            stmt.author = author;
        if (at !== undefined)
            stmt.at = at;
        return stmt;
    }
    parseUpdateRef(start) {
        const ref = this.parseNameOrHash();
        if (ref.kind === 'name' && ref.parts.length !== 1) {
            this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'Expected bound group reference, not group.table', ref.span);
        }
        this.expectKeyword('TO');
        const version = this.parseVersion();
        this.expectKeyword('ON');
        const group = this.parseNameOrHash();
        let end = group.span;
        let at;
        let author;
        while (!this.isEof() && !this.checkPunctuation(';') && !this.checkPunctuation(')')) {
            if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('BY')) {
                author = this.parseAuthor();
                end = author.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected UPDATE REF clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'update-ref', ref, version, group, span: combineSpans(start, end) };
        if (author !== undefined)
            stmt.author = author;
        if (at !== undefined)
            stmt.at = at;
        return stmt;
    }
    parseInsert(start, defaultGroup) {
        this.expectKeyword('INTO');
        const table = this.parseTableRef(defaultGroup);
        this.expectPunctuation('(');
        const columns = this.parseIdentifierList('column');
        this.expectPunctuation(')');
        this.expectKeyword('VALUES');
        this.expectPunctuation('(');
        const values = [];
        if (!this.checkPunctuation(')')) {
            do {
                values.push(this.parseValue());
            } while (this.matchPunctuation(','));
        }
        let end = this.expectPunctuation(')').span;
        let at;
        let author;
        while (!this.isEof() && !this.checkPunctuation(';') && !this.checkPunctuation(')')) {
            if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('BY')) {
                author = this.parseAuthor();
                end = author.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected INSERT clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'insert', table, columns, values, span: combineSpans(start, end) };
        if (author !== undefined)
            stmt.author = author;
        if (at !== undefined)
            stmt.at = at;
        return stmt;
    }
    parseUpdate(start, defaultGroup) {
        const table = this.parseTableRef(defaultGroup);
        this.expectKeyword('SET');
        const values = [];
        do {
            const column = this.expectIdentifierText('updated column');
            this.expectOperator('=');
            values.push({ column, value: this.parseValue() });
        } while (this.matchPunctuation(','));
        this.expectKeyword('WHERE');
        const rowId = this.parseRowIdPredicate();
        let end = rowId.span;
        let at;
        let author;
        while (!this.isEof() && !this.checkPunctuation(';') && !this.checkPunctuation(')')) {
            if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('BY')) {
                author = this.parseAuthor();
                end = author.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected UPDATE clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'update', table, values, rowId, span: combineSpans(start, end) };
        if (author !== undefined)
            stmt.author = author;
        if (at !== undefined)
            stmt.at = at;
        return stmt;
    }
    parseDelete(start, defaultGroup) {
        this.expectKeyword('FROM');
        const table = this.parseTableRef(defaultGroup);
        this.expectKeyword('WHERE');
        const rowId = this.parseRowIdPredicate();
        let end = rowId.span;
        let at;
        let author;
        while (!this.isEof() && !this.checkPunctuation(';') && !this.checkPunctuation(')')) {
            if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('BY')) {
                author = this.parseAuthor();
                end = author.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected DELETE clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'delete', table, rowId, span: combineSpans(start, end) };
        if (author !== undefined)
            stmt.author = author;
        if (at !== undefined)
            stmt.at = at;
        return stmt;
    }
    parseBundle(start) {
        this.expectKeyword('ON');
        const group = this.parseNameOrHash();
        this.expectPunctuation('(');
        const writes = [];
        while (!this.checkPunctuation(')') && !this.isEof()) {
            let write;
            if (this.matchKeyword('INSERT'))
                write = this.parseInsert(this.previous().span, group);
            else if (this.matchKeyword('UPDATE'))
                write = this.parseUpdate(this.previous().span, group);
            else if (this.matchKeyword('DELETE'))
                write = this.parseDelete(this.previous().span, group);
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected BUNDLE statement '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
            if (write !== undefined) {
                // A bundle is a single signed op with one author; put BY on the
                // BUNDLE, not on individual writes.
                if (write.author !== undefined) {
                    this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', 'BY is not allowed on a bundle inner write; put BY on the BUNDLE', write.author.span);
                    delete write.author;
                }
                writes.push(write);
            }
            this.matchPunctuation(';');
        }
        let end = this.expectPunctuation(')').span;
        let at;
        let author;
        while (!this.isEof() && !this.checkPunctuation(';')) {
            if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('BY')) {
                author = this.parseAuthor();
                end = author.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected BUNDLE clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'bundle', group, writes, span: combineSpans(start, end) };
        if (author !== undefined)
            stmt.author = author;
        if (at !== undefined)
            stmt.at = at;
        return stmt;
    }
    parseSetView(start) {
        this.expectKeyword('AT');
        const at = this.parseVersion();
        let end = at.span;
        let from;
        if (this.matchKeyword('FROM')) {
            from = this.parseVersion();
            end = from.span;
        }
        const stmt = { kind: 'set-view', at, span: combineSpans(start, end) };
        if (from !== undefined)
            stmt.from = from;
        return stmt;
    }
    parseSelect(start) {
        let projection;
        if (this.matchOperator('*')) {
            projection = '*';
        }
        else {
            projection = this.parseIdentifierList('projection');
        }
        this.expectKeyword('FROM');
        const table = this.parseTableRef();
        let where;
        const orderBy = [];
        let limit;
        let offset;
        let at;
        let from;
        let end = table.span;
        while (!this.isEof() && !this.checkPunctuation(';')) {
            if (this.matchKeyword('WHERE')) {
                where = this.parsePredicate();
                end = where.span;
            }
            else if (this.matchKeyword('ORDER')) {
                this.expectKeyword('BY');
                do {
                    const col = this.expectIdentifierToken('order column');
                    let dir;
                    if (this.matchKeyword('ASC'))
                        dir = 'asc';
                    else if (this.matchKeyword('DESC'))
                        dir = 'desc';
                    const item = { column: col.text, span: col.span };
                    if (dir !== undefined)
                        item.dir = dir;
                    orderBy.push(item);
                    end = col.span;
                } while (this.matchPunctuation(','));
            }
            else if (this.matchKeyword('LIMIT')) {
                limit = this.expectInteger('LIMIT');
                end = this.previous().span;
            }
            else if (this.matchKeyword('OFFSET')) {
                offset = this.expectInteger('OFFSET');
                end = this.previous().span;
            }
            else if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('FROM')) {
                from = this.parseVersion();
                end = from.span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected SELECT clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'select', projection, table, orderBy, span: combineSpans(start, end) };
        if (where !== undefined)
            stmt.where = where;
        if (limit !== undefined)
            stmt.limit = limit;
        if (offset !== undefined)
            stmt.offset = offset;
        if (at !== undefined)
            stmt.at = at;
        if (from !== undefined)
            stmt.from = from;
        return stmt;
    }
    parseLog(start) {
        const target = this.parseNameOrHash();
        let at;
        let from;
        let limit;
        let offset;
        let end = target.span;
        while (!this.isEof() && !this.checkPunctuation(';')) {
            if (this.matchKeyword('AT')) {
                at = this.parseVersion();
                end = at.span;
            }
            else if (this.matchKeyword('FROM')) {
                from = this.parseVersion();
                end = from.span;
            }
            else if (this.matchKeyword('LIMIT')) {
                limit = this.expectInteger('LIMIT');
                end = this.previous().span;
            }
            else if (this.matchKeyword('OFFSET')) {
                offset = this.expectInteger('OFFSET');
                end = this.previous().span;
            }
            else {
                this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', `Unexpected LOG clause '${this.peek().text}'`, this.peek().span);
                this.advance();
            }
        }
        const stmt = { kind: 'log', target, span: combineSpans(start, end) };
        if (at !== undefined)
            stmt.at = at;
        if (from !== undefined)
            stmt.from = from;
        if (limit !== undefined)
            stmt.limit = limit;
        if (offset !== undefined)
            stmt.offset = offset;
        return stmt;
    }
    parsePredicate() {
        return this.parseOr();
    }
    parseOr() {
        let expr = this.parseAnd();
        while (this.matchKeyword('OR')) {
            const right = this.parseAnd();
            expr = expr.kind === 'or'
                ? Object.assign(Object.assign({}, expr), { args: [...expr.args, right], span: combineSpans(expr.span, right.span) }) : { kind: 'or', args: [expr, right], span: combineSpans(expr.span, right.span) };
        }
        return expr;
    }
    parseAnd() {
        let expr = this.parseNot();
        while (this.matchKeyword('AND')) {
            const right = this.parseNot();
            expr = expr.kind === 'and'
                ? Object.assign(Object.assign({}, expr), { args: [...expr.args, right], span: combineSpans(expr.span, right.span) }) : { kind: 'and', args: [expr, right], span: combineSpans(expr.span, right.span) };
        }
        return expr;
    }
    parseNot() {
        if (this.matchKeyword('NOT')) {
            const start = this.previous().span;
            const arg = this.parseNot();
            return { kind: 'not', arg, span: combineSpans(start, arg.span) };
        }
        return this.parsePredicatePrimary();
    }
    parsePredicatePrimary() {
        if (this.matchPunctuation('(')) {
            const start = this.previous().span;
            const expr = this.parsePredicate();
            const end = this.expectPunctuation(')').span;
            return Object.assign(Object.assign({}, expr), { span: combineSpans(start, end) });
        }
        if (this.matchKeyword('TRUE'))
            return { kind: 'true', span: this.previous().span };
        if (this.matchKeyword('FALSE'))
            return { kind: 'false', span: this.previous().span };
        if (this.matchKeyword('EXISTS')) {
            const start = this.previous().span;
            const table = this.expectIdentifierToken('EXISTS table');
            let alias;
            if (this.matchKeyword('AS')) {
                alias = this.expectIdentifierToken('EXISTS alias').text;
            }
            let where;
            let end = alias !== undefined ? this.previous().span : table.span;
            if (this.matchKeyword('WHERE')) {
                where = this.parsePredicate();
                end = where.span;
            }
            if (where === undefined) {
                this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'EXISTS requires a WHERE clause', table.span);
                where = { kind: 'false', span: table.span };
            }
            return { kind: 'exists', table: table.text, alias, where, span: combineSpans(start, end) };
        }
        const left = this.parseOperand();
        if (this.matchKeyword('LIKE')) {
            const pattern = this.parseValue();
            return { kind: 'like', left, pattern, span: combineSpans(left.span, pattern.span) };
        }
        if (this.checkKind('operator') && ['=', '!=', '<', '<=', '>', '>='].includes(this.peek().text)) {
            const op = this.advance().text;
            const right = this.parseOperand();
            return { kind: 'comparison', op, left, right, span: combineSpans(left.span, right.span) };
        }
        this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'Expected predicate comparison', left.span);
        return { kind: 'true', span: left.span };
    }
    parseOperand() {
        if (this.checkKind('identifier')) {
            const tok = this.advance();
            const dot = tok.text.lastIndexOf('.');
            if (dot >= 0) {
                return {
                    kind: 'column',
                    table: tok.text.substring(0, dot),
                    name: tok.text.substring(dot + 1),
                    span: tok.span,
                };
            }
            return { kind: 'column', name: tok.text, span: tok.span };
        }
        return this.parseValue();
    }
    parseValue() {
        const tok = this.peek();
        if (tok.kind === 'identifier' && this.peekNext().text === '(') {
            return this.parseValueCall();
        }
        if (tok.kind === 'string' || tok.kind === 'number' ||
            (tok.kind === 'keyword' && ['TRUE', 'FALSE', 'NULL'].includes(tok.upper))) {
            this.advance();
            return { kind: 'literal', value: tok.value, span: tok.span };
        }
        if (tok.kind === 'variable') {
            this.advance();
            const raw = tok.text.substring(1);
            const dot = raw.indexOf('.');
            if (dot === -1)
                return { kind: 'variable', name: raw, span: tok.span };
            return {
                kind: 'variable',
                name: raw.substring(0, dot),
                field: raw.substring(dot + 1),
                span: tok.span,
            };
        }
        if (this.checkKind('hash')) {
            const hashTok = this.advance();
            return { kind: 'hash', prefix: hashTok.text.substring(1), span: hashTok.span };
        }
        if (this.checkPunctuation('[') || this.checkPunctuation('{')) {
            return this.parseJsonValue();
        }
        this.diagnostics.add('PARSE_EXPECTED_TOKEN', `Expected literal or variable, got '${tok.text}'`, tok.span);
        this.advance();
        return { kind: 'literal', value: null, span: tok.span };
    }
    parseValueCall() {
        const name = this.expectIdentifierToken('value function');
        this.expectPunctuation('(');
        const args = [];
        if (!this.checkPunctuation(')')) {
            do {
                args.push(this.parseValue());
            } while (this.matchPunctuation(','));
        }
        const end = this.expectPunctuation(')').span;
        if (name.text !== 'publicKey') {
            this.diagnostics.add('PARSE_EXPECTED_TOKEN', `Unknown value function '${name.text}'`, name.span);
        }
        if (name.text === 'publicKey' && args.length !== 1) {
            this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'publicKey() expects exactly one argument', combineSpans(name.span, end));
        }
        return { kind: 'call', name: name.text, args, span: combineSpans(name.span, end) };
    }
    parseJsonValue() {
        const start = this.peek();
        const open = start.text;
        const close = open === '[' ? ']' : '}';
        let depth = 0;
        let raw = '';
        let end = start.span;
        do {
            const tok = this.advance();
            raw += tok.text;
            end = tok.span;
            if (tok.text === open)
                depth += 1;
            if (tok.text === close)
                depth -= 1;
        } while (!this.isEof() && depth > 0);
        try {
            return { kind: 'literal', value: JSON.parse(raw), span: combineSpans(start.span, end) };
        }
        catch (_a) {
            this.diagnostics.add('PARSE_UNEXPECTED_TOKEN', 'Invalid JSON literal', combineSpans(start.span, end));
            return { kind: 'literal', value: null, span: combineSpans(start.span, end) };
        }
    }
    parseAuthor() {
        const start = this.peek().span;
        if (this.matchKeyword('NOBODY'))
            return { kind: 'nobody', span: this.previous().span };
        if (this.checkKind('variable')) {
            const tok = this.advance();
            return { kind: 'variable', name: tok.text.substring(1), span: tok.span };
        }
        if (this.checkKind('hash')) {
            const tok = this.advance();
            return { kind: 'hash', prefix: tok.text.substring(1), span: tok.span };
        }
        this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'Expected $identity, #keyid or NOBODY after BY', start);
        return { kind: 'nobody', span: start };
    }
    parseVersion() {
        if (this.matchKeyword('LATEST'))
            return { kind: 'latest', span: this.previous().span };
        if (this.checkKind('hash')) {
            const hash = this.parseHashRef();
            return { kind: 'hash', hash, span: hash.span };
        }
        const start = this.expectPunctuation('{').span;
        const members = [];
        if (!this.checkPunctuation('}')) {
            do {
                members.push(this.parseVersionMember());
            } while (this.matchPunctuation(','));
        }
        const end = this.expectPunctuation('}').span;
        return { kind: 'set', members, span: combineSpans(start, end) };
    }
    parseVersionMember() {
        if (this.checkKind('hash'))
            return this.parseHashRef();
        const tok = this.expectIdentifierToken('version member');
        return this.nameRef(tok.text, tok.span);
    }
    parseRowIdPredicate() {
        const field = this.expectIdentifierToken('row id field');
        if (field.text !== 'rowId') {
            this.diagnostics.add('PARSE_EXPECTED_TOKEN', "Expected rowId predicate", field.span);
        }
        this.expectOperator('=');
        if (this.checkKind('hash'))
            return this.parseHashRef();
        if (this.checkKind('identifier')) {
            const tok = this.advance();
            return this.nameRef(tok.text, tok.span);
        }
        return this.parseValue();
    }
    parseQualifiedColumn() {
        const tok = this.expectIdentifierToken('qualified column');
        const parts = tok.text.split('.');
        if (parts.length !== 2) {
            this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'Expected qualified column in the form table.column', tok.span);
            return { table: tok.text, column: tok.text, span: tok.span };
        }
        return { table: parts[0], column: parts[1], span: tok.span };
    }
    parseTableRef(defaultGroup) {
        const tok = this.expectIdentifierToken('table reference');
        const parts = tok.text.split('.');
        if (parts.length === 1 && defaultGroup !== undefined) {
            return { group: defaultGroup, table: tok.text, span: combineSpans(defaultGroup.span, tok.span) };
        }
        if (parts.length === 1) {
            return { table: tok.text, span: tok.span };
        }
        if (parts.length !== 2) {
            this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'Expected table reference in the form group.table', tok.span);
        }
        const groupText = parts.length >= 2 ? parts.slice(0, parts.length - 1).join('.') : tok.text;
        const table = parts.length >= 2 ? parts[parts.length - 1] : tok.text;
        return { group: this.nameRef(groupText, tok.span), table, span: tok.span };
    }
    parseNameOrHash() {
        if (this.checkKind('hash'))
            return this.parseHashRef();
        const tok = this.expectIdentifierToken('name or hash reference');
        return this.nameRef(tok.text, tok.span);
    }
    parseHashRef() {
        const tok = this.expectKind('hash', 'hash reference');
        return { kind: 'hash', prefix: tok.text.substring(1), span: tok.span };
    }
    nameRef(text, span) {
        return { kind: 'name', text, parts: text.split('.'), span };
    }
    parseIdentifierList(label) {
        const items = [];
        if (!this.checkPunctuation(')') && !this.checkKeyword('FROM')) {
            do {
                items.push(this.expectIdentifierText(label));
            } while (this.matchPunctuation(','));
        }
        return items;
    }
    columnTypeFromToken(tok) {
        const type = tok.upper.toLowerCase();
        if (['string', 'integer', 'float', 'boolean', 'json'].includes(type))
            return type;
        this.diagnostics.add('PARSE_EXPECTED_TOKEN', `Expected column type, got '${tok.text}'`, tok.span);
        return 'string';
    }
    parseAllowRule() {
        const start = this.expectKeyword('ALLOW').span;
        const op = this.opTag(this.expectIdentifierLike('allow op').text);
        this.expectKeyword('IF');
        const predicate = this.parsePredicate();
        return { op, predicate, span: combineSpans(start, predicate.span) };
    }
    validateAllowRules(rules) {
        const seen = new Map();
        const allRule = rules.find((rule) => rule.op === 'all');
        const specificRule = rules.find((rule) => rule.op !== 'all');
        for (const rule of rules) {
            if (seen.has(rule.op)) {
                this.diagnostics.add('PARSE_EXPECTED_TOKEN', `Duplicate ALLOW ${rule.op} rule`, rule.span);
            }
            seen.set(rule.op, rule.span);
        }
        if (allRule !== undefined && specificRule !== undefined) {
            this.diagnostics.add('PARSE_EXPECTED_TOKEN', 'ALLOW all cannot be combined with operation-specific ALLOW rules', specificRule.span);
        }
    }
    opTag(text) {
        const tag = text.toLowerCase();
        if (tag === 'insert' || tag === 'update' || tag === 'delete' || tag === 'all')
            return tag;
        this.diagnostics.add('PARSE_EXPECTED_TOKEN', `Unknown allow op '${text}'`, this.previous().span);
        return 'all';
    }
    expectInteger(label) {
        const tok = this.expectKind('number', `${label} integer`);
        const n = Number(tok.value);
        if (!Number.isInteger(n) || n < 0) {
            this.diagnostics.add('PARSE_EXPECTED_TOKEN', `${label} must be a non-negative integer`, tok.span);
            return 0;
        }
        return n;
    }
    consumeSemicolons() {
        while (this.matchPunctuation(';')) { }
    }
    synchronizeStatement() {
        while (!this.isEof() && !this.checkPunctuation(';'))
            this.advance();
        this.matchPunctuation(';');
    }
    expected(expected) {
        this.diagnostics.add('PARSE_EXPECTED_TOKEN', `Expected ${expected}, got '${this.peek().text}'`, this.peek().span);
    }
    expectKeyword(keyword) {
        if (this.matchKeyword(keyword))
            return this.previous();
        this.expected(keyword);
        return this.peek();
    }
    expectPunctuation(text) {
        if (this.matchPunctuation(text))
            return this.previous();
        this.expected(`'${text}'`);
        return this.peek();
    }
    expectOperator(text) {
        if (this.matchOperator(text))
            return this.previous();
        this.expected(`'${text}'`);
        return this.peek();
    }
    expectIdentifierText(label) {
        return this.expectIdentifierToken(label).text;
    }
    expectIdentifierToken(label) {
        return this.expectKind('identifier', label);
    }
    expectIdentifierLike(label) {
        if (this.checkKind('identifier') || this.checkKind('keyword'))
            return this.advance();
        this.expected(label);
        return this.peek();
    }
    expectKind(kind, label) {
        if (this.checkKind(kind))
            return this.advance();
        this.expected(label);
        return this.peek();
    }
    matchKeyword(keyword) {
        if (this.checkKeyword(keyword)) {
            this.advance();
            return true;
        }
        return false;
    }
    matchPunctuation(text) {
        if (this.checkPunctuation(text)) {
            this.advance();
            return true;
        }
        return false;
    }
    matchOperator(text) {
        if (this.checkKind('operator') && this.peek().text === text) {
            this.advance();
            return true;
        }
        return false;
    }
    checkKeyword(keyword) {
        return this.peek().kind === 'keyword' && this.peek().upper === keyword;
    }
    checkPunctuation(text) {
        return this.peek().kind === 'punctuation' && this.peek().text === text;
    }
    checkKind(kind) {
        return this.peek().kind === kind;
    }
    isEof() {
        return this.peek().kind === 'eof';
    }
    peek() {
        return this.tokens[this.pos];
    }
    peekNext() {
        var _a;
        return (_a = this.tokens[this.pos + 1]) !== null && _a !== void 0 ? _a : this.tokens[this.tokens.length - 1];
    }
    previous() {
        return this.tokens[this.pos - 1];
    }
    advance() {
        if (!this.isEof())
            this.pos += 1;
        return this.previous();
    }
}
export function parseScript(text) {
    const lexed = lex(text);
    if (!lexed.ok)
        return lexed;
    return new Parser(text, lexed.value).parseScript();
}
export function parseStatement(text) {
    const parsed = parseScript(text);
    if (!parsed.ok)
        return parsed;
    if (parsed.value.statements.length !== 1) {
        const diagnostics = new DiagnosticBag();
        diagnostics.add('PARSE_EXPECTED_TOKEN', `Expected one statement, got ${parsed.value.statements.length}`, parsed.value.span);
        return err(diagnostics.all());
    }
    return ok(parsed.value.statements[0]);
}
